import {
  getDownloadStartListCsv,
  getDownloadStartListPdf,
  getStartListsSummary,
} from 'api/eventStartListApi';
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styles from './StartListsView.module.css';
import filterStyles from 'components/admin/bulkAdminTable.module.css';
import CustomNewButton from 'components/NewDesign/CustomNewButton';
import MultiSelectFilter from 'components/admin/MultiSelectFilter';
import routes from 'routes';
import { getMeetSummary } from 'api/clubApi';
import { convertDateTime } from 'utils/time';
import Loading from 'components/common/Loading';
import AdminTablePageLayout from 'components/admin/AdminTablePageLayout';
import MeetPageHeader from 'components/NewDesign/MeetPageHeader';
import { getUserPermissionLevel } from 'services/auth/tokenService';
import { UserPermissionLevel } from 'constants';
import { Download } from 'lucide-react';
import { exportAndDownloadFile } from 'utils/fileDownload';
import { activeAgeFilter } from 'utils/ageFilter';
import useGroupLabels from 'hooks/useGroupLabels';
import useSessionFilters from 'hooks/useSessionFilters';

const GENDER_OPTIONS = [
  { value: 'all', label: 'All genders' },
  { value: 'M', label: 'Male' },
  { value: 'F', label: 'Female' },
];

// The youngest age of a row's group, read from its range ("U10", "13-17").
const startAgeOf = (ageGroup) => {
  const match = ageGroup?.match(/(\d+)/);
  return match ? parseInt(match[1], 10) : 999;
};

// The events in the rows, by name, in the order the server lists them.
const eventOptionsOf = (rows) => [
  ...new Set(rows.map((row) => row.eventTypeName).filter(Boolean)),
];

// The groups in the rows, by name, youngest first. A start list whose group
// has left the meet program has no name, so it is not offered; its row shows
// while no group is ticked.
const groupOptionsOf = (rows) => {
  const startAgeByName = {};
  rows.forEach((row) => {
    if (!row.groupName) return;
    const startAge = startAgeOf(row.ageGroup);
    if (!(row.groupName in startAgeByName) || startAge < startAgeByName[row.groupName]) {
      startAgeByName[row.groupName] = startAge;
    }
  });
  return Object.keys(startAgeByName)
    .sort((a, b) => startAgeByName[a] - startAgeByName[b])
    .map((name) => ({ value: name, label: name }));
};

const StartListsView = () => {
  const { clubId, meetId } = useParams();
  const navigate = useNavigate();
  // The filter and the column read Age Group, or Year Group for a club that groups by year level.
  const { words } = useGroupLabels();

  // Filters, kept for the tab's session so they survive a visit to a start
  // list and back. `groups` is the ticked group names; empty means every group.
  const [filters, setFilter] = useSessionFilters(
    `startListsViewFilters_${meetId}`,
    { event: 'all', groups: [], gender: 'all' }
  );
  const [meetDetail, setMeetDetail] = useState(null);

  const userPermissionLevel = getUserPermissionLevel();

  const [fetchingStartListsSummary, setFetchingStartListsSummary] =
    useState(true);
  const [startListsSummary, setStartListsSummary] = useState([]);

  const eventOptions = useMemo(
    () => eventOptionsOf(startListsSummary),
    [startListsSummary]
  );
  const groupOptions = useMemo(
    () => groupOptionsOf(startListsSummary),
    [startListsSummary]
  );

  // The choices in force: a remembered choice that the meet no longer offers
  // is dropped, so the controls and the rows always agree.
  const eventFilter = eventOptions.includes(filters.event) ? filters.event : 'all';
  const groupFilter = useMemo(
    () => activeAgeFilter(filters.groups, groupOptions),
    [filters.groups, groupOptions]
  );
  const genderFilter = filters.gender;

  const fetchStartListsSummary = useCallback(async () => {
    try {
      setFetchingStartListsSummary(true);
      const response = await getStartListsSummary(meetId);
      //console.log('[StartListsView] Fetch start lists summary response: ', response.data);
      setStartListsSummary(response?.data || []);
    } catch (err) {
      console.log('[ShowMeet] Fetch start lists summary error: ', err);
    } finally {
      setFetchingStartListsSummary(false);
    }
  }, [meetId]);

  useEffect(() => {
    fetchStartListsSummary();
  }, [fetchStartListsSummary]);

  const fetchMeetDetail = useCallback(async () => {
    try {
      const response = await getMeetSummary(clubId, meetId);
      if (response?.data) {
        setMeetDetail({
          ...response.data,
          meetDate: convertDateTime(response.data.meetDate).date,
        });
      }
    } catch (err) {
      console.log('[ShowMeet] Fetch start lists summary error: ', err);
    }
  }, [meetId, clubId]);

  useEffect(() => {
    fetchMeetDetail();
  }, [fetchMeetDetail]);

  const onClickView = (eventTypeCode, ageGroup, gender) => {
    navigate(
      routes.admin.meetStartListDetails.url(
        clubId,
        meetId,
        eventTypeCode,
        ageGroup,
        gender
      )
    );
  };

  const onClickEdit = (eventTypeCode, ageGroup, gender, startListId) => {
    navigate(
      routes.admin.meetStartListEditor.url(
        clubId,
        meetId,
        eventTypeCode,
        ageGroup,
        gender,
        startListId
      )
    );
  };

  // Filter events
  const filteredEvents = useMemo(() => {
    const tickedGroups = new Set(groupFilter);
    return startListsSummary
      .filter(
        (event) =>
          (eventFilter === 'all' || event.eventTypeName === eventFilter) &&
          (tickedGroups.size === 0 || tickedGroups.has(event.groupName)) &&
          (genderFilter === 'all' || event.gender === genderFilter)
      )
      .sort((a, b) => startAgeOf(a.ageGroup) - startAgeOf(b.ageGroup));
  }, [startListsSummary, eventFilter, groupFilter, genderFilter]);

  // The PDF and the CSV hold the start lists in the rows showing, so they
  // follow the filters. A row with no start list yet has nothing to put in a file.
  const downloadStartLists = async (apiCall, extension) => {
    const startListIds = filteredEvents
      .map((event) => event.eventStartListID)
      .filter(Boolean);
    if (startListIds.length === 0) {
      toast.info('None of the events listed has a start list to download.');
      return;
    }
    const meetName = (meetDetail?.meetName || 'meet').replace(/\s+/g, '_').toLowerCase();
    await exportAndDownloadFile(
      apiCall,
      [meetId, startListIds],
      `${meetName}_start_list.${extension}`,
      'The start list was successfully exported',
      'Fetching start list data from the server...'
    );
  };

  return (
    <AdminTablePageLayout loading={fetchingStartListsSummary && !meetDetail}>
      <MeetPageHeader
        title="Start Lists"
        meetName={meetDetail?.meetName}
        meetDate={meetDetail?.meetDate}
        onBackToMeet={() => navigate(routes.admin.showMeet.url(clubId, meetId))}
      >
        {userPermissionLevel >= UserPermissionLevel.Official && (
          <>
            <CustomNewButton
              variant="primary2"
              customStyle={{ gap: '8px' }}
              onClick={() => downloadStartLists(getDownloadStartListPdf, 'pdf')}
            >
              <Download size={16} />
              Download PDF
            </CustomNewButton>
            <CustomNewButton
              variant="primary2"
              customStyle={{ gap: '8px' }}
              onClick={() => downloadStartLists(getDownloadStartListCsv, 'csv')}
            >
              <Download size={16} />
              Download CSV
            </CustomNewButton>
          </>
        )}
      </MeetPageHeader>

      {/* Filter card: the same controls as the athlete and results lists. */}
      <div className={`${filterStyles.filterCard} ${styles.startListsFilterCard}`}>
        <div className={filterStyles.filterGroup}>
          <label className={filterStyles.filterLabel}>Event</label>
          <select
            className={filterStyles.filterSelect}
            value={eventFilter}
            onChange={(e) => setFilter('event', e.target.value)}
          >
            <option value="all">All events</option>
            {eventOptions.map((eventName) => (
              <option key={eventName} value={eventName}>
                {eventName}
              </option>
            ))}
          </select>
        </div>
        <div className={filterStyles.filterGroup}>
          <label className={filterStyles.filterLabel}>{words.group}</label>
          <MultiSelectFilter
            value={groupFilter}
            options={groupOptions}
            onChange={(next) => setFilter('groups', next)}
            allLabel={`All ${words.lower.groups}`}
            itemLabel={words.lower.group}
          />
        </div>
        <div className={filterStyles.filterGroup}>
          <label className={filterStyles.filterLabel}>Gender</label>
          <select
            className={filterStyles.filterSelect}
            value={genderFilter}
            onChange={(e) => setFilter('gender', e.target.value)}
          >
            {GENDER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Events Table Card */}
      <div className={styles.tableCard}>
        {fetchingStartListsSummary ? (
          <div style={{ padding: '40px' }}>
            <Loading isLoadingAdminPage={true} />
          </div>
        ) : (
          <>
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr className={styles.tableHeader}>
                    <th className={styles.tableHeaderCell}>Event</th>
                    <th className={styles.tableHeaderCell}>{words.group}</th>
                    <th
                      className={`${styles.tableHeaderCell} ${styles.tableHeaderCellCenter}`}
                    >
                      Athletes
                    </th>
                    <th
                      className={`${styles.tableHeaderCell} ${styles.tableHeaderCellCenter}`}
                    >
                      Heats
                    </th>
                    <th
                      className={`${styles.tableHeaderCell} ${styles.tableHeaderCellCenter}`}
                    >
                      Semis
                    </th>
                    <th
                      className={`${styles.tableHeaderCell} ${styles.tableHeaderCellCenter}`}
                    >
                      Finals
                    </th>
                    <th
                      className={`${styles.tableHeaderCell} ${styles.tableHeaderCellCenter}`}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEvents.length === 0 ? (
                    <tr>
                      <td colSpan="7" className={styles.emptyState}>
                        No events found matching your filters
                      </td>
                    </tr>
                  ) : (
                    filteredEvents.map((event, index) => (
                      <tr
                        key={`${event.eventTypeCode}-${event.ageGroup}-${event.gender}-${index}`}
                        className={styles.tableRow}
                      >
                        <td className={styles.tableCell}>
                          {event.eventTypeName}
                        </td>
                        <td className={styles.tableCell}>
                          {event.groupName} {event.gender}
                        </td>
                        <td
                          className={`${styles.tableCell} ${styles.tableCellCenter}`}
                        >
                          <span className={styles.athleteCount}>
                            {event.registeredAthletes || 0}
                          </span>
                        </td>

                    {/* HEATS COLUMN */}
<td className={`${styles.tableCell} ${styles.tableCellCenter}`}>
  {event.heatsCount > 0 ? (
    <span
      className={`${styles.statusBadge} ${
        event.heatsAssignedAthletes < event.registeredAthletes
          ? styles.statusBadgeWarning
          : ''
      }`}
    >
      {event.heatsAssignedAthletes >= event.registeredAthletes ? (
        // Fully assigned: ✓ 3 (33)
        <>
          ✓ {event.heatsCount}
          {event.heatsAssignedAthletes > 0 && (
            <span className={styles.assignedCount}>
              ({event.heatsAssignedAthletes})
            </span>
          )}
        </>
      ) : (
        // Not fully assigned: 3 (28/33)
        <>
          {event.heatsCount}
          {event.heatsAssignedAthletes > 0 && (
            <span className={styles.assignedCount}>
              ({event.heatsAssignedAthletes}/{event.registeredAthletes})
            </span>
          )}
        </>
      )}
    </span>
  ) : (
    <span className={styles.statusEmpty}>○</span>
  )}
</td>

{/* SEMIS COLUMN */}
<td className={`${styles.tableCell} ${styles.tableCellCenter}`}>
  {event.semisCount > 0 ? (
    <span className={styles.statusBadge}>
      ✓ {event.semisCount}
      {event.semisAssignedAthletes > 0 && (
        <span className={styles.assignedCount}>
          ({event.semisAssignedAthletes})
        </span>
      )}
    </span>
  ) : (
    <span className={styles.statusEmpty}>○</span>
  )}
</td>

{/* FINALS COLUMN */}
<td className={`${styles.tableCell} ${styles.tableCellCenter}`}>
  {event.finalsCount > 0 ? (
    <span className={styles.statusBadge}>
      ✓ {event.finalsCount}
      {event.finalsAssignedAthletes > 0 && (
        <span className={styles.assignedCount}>
          ({event.finalsAssignedAthletes})
        </span>
      )}
    </span>
  ) : (
    <span className={styles.statusEmpty}>○</span>
  )}
</td>

                        <td
                          className={`${styles.tableCell} ${styles.tableCellCenter}`}
                        >
                          <div className={styles.actionButtons}>
                            <CustomNewButton
                              variant="outline"
                              small
                              onClick={() =>
                                onClickView(
                                  event.eventTypeCode,
                                  event.ageGroup,
                                  event.gender
                                )
                              }
                            >
                              View
                            </CustomNewButton>
                            {userPermissionLevel >= UserPermissionLevel.Official && (
                              <CustomNewButton
                                variant=""
                                small
                                customStyle={{ 
                                  backgroundColor: '#fef3c7',
                                  color: '#92400e',
                                  border: '1px solid #fbbf24'
                                }}
                                onClick={() =>
                                  onClickEdit(
                                    event.eventTypeCode,
                                    event.ageGroup,
                                    event.gender,
                                    event.eventStartListID
                                  )
                                }
                              >
                                Edit
                              </CustomNewButton>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className={styles.summaryInfo}>
              Showing <strong>{filteredEvents.length}</strong> events
            </div>
          </>
        )}
      </div>
    </AdminTablePageLayout>
  );
};

export default StartListsView;